import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Loading from "../common/Loading";
import { ACCENT, Field, PrimaryButton, SecondaryButton, errorMessage } from "./shared";
import useStudioAccount from "./useStudioAccount";
import StudioGate from "./StudioGate";
import uploadStudioFiles from "./studioUpload";
import {
  DEFAULT_FAN_RULES,
  checkCoverFile,
  checkCoverSize,
  coverGuidance,
  fanFundingApi,
  formatFanDate,
  formatJoinedDate,
  MESSAGE_STATE_TEXT,
  REPORT_REASON_MAX,
  readImageSize,
  supporterHandle,
  validateBio,
} from "./fanFundingApi";

// Artist Studio > Fan Funding, in three tabs: Fan Page (cover image and bio, editable), Funding Received and Subscribers
// (system-managed, read-only lists from the server).
const TABS = [
  { key: "page", label: "Fan Page" },
  { key: "funding", label: "Funding Received" },
  { key: "subscribers", label: "Subscribers" },
];
const money = (n, currency = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(n) || 0);
const TH = "px-6 py-3 text-xs font-bold uppercase tracking-wide";

function Tabs({ active, onChange }) {
  return (
    <div className="flex gap-8 mb-8" role="tablist" aria-label="Fan Funding sections">
      {TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          role="tab"
          aria-selected={active === t.key}
          onClick={() => onChange(t.key)}
          className={`text-[15px] font-semibold pb-1 ${active === t.key ? "" : "text-gray-500 hover:text-gray-800"}`}
          style={active === t.key ? { color: ACCENT } : undefined}
          data-testid={`tab-${t.key}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function FanPageTab({ saved, onSaved }) {
  const [bio, setBio] = useState(saved.bio || "");
  const [cover, setCover] = useState(null); // a newly chosen, not yet saved file: { file, previewUrl, width, height }
  const [removeCover, setRemoveCover] = useState(false);
  const [errors, setErrors] = useState({});
  const [guidance, setGuidance] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const mounted = useRef(true);
  const rules = saved.rules || DEFAULT_FAN_RULES;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  useEffect(() => () => cover && URL.revokeObjectURL(cover.previewUrl), [cover]);

  const shownCover = removeCover ? "" : cover ? cover.previewUrl : saved.cover_image_url || "";
  const dirty = bio.trim() !== (saved.bio || "") || !!cover || removeCover;

  const choose = async (file) => {
    if (!file || saving) return;
    setGuidance("");
    const problem = checkCoverFile(file, rules);
    if (problem) {
      setErrors((e) => ({ ...e, cover: problem }));
      return;
    }
    try {
      const { width, height, previewUrl } = await readImageSize(file);
      const sizeProblem = checkCoverSize(width, height, rules);
      if (sizeProblem) {
        URL.revokeObjectURL(previewUrl);
        setErrors((e) => ({ ...e, cover: sizeProblem }));
        return;
      }
      setErrors((e) => ({ ...e, cover: undefined }));
      setGuidance(coverGuidance(width, height, rules));
      setRemoveCover(false);
      setCover({ file, previewUrl, width, height });
    } catch (e) {
      setErrors((prev) => ({ ...prev, cover: e.message }));
    }
  };

  const cancel = () => {
    setBio(saved.bio || "");
    setCover(null);
    setRemoveCover(false);
    setErrors({});
    setGuidance("");
  };

  const save = async () => {
    if (saving) return;
    const found = {};
    const bioProblem = validateBio(bio, rules);
    if (bioProblem) found.bio = bioProblem;
    setErrors(found);
    if (Object.keys(found).length) return;
    if (!dirty) {
      toast.info("There are no changes to save.");
      return;
    }
    const body = {};
    if (bio.trim() !== (saved.bio || "")) body.bio = bio.trim();
    setSaving(true);
    try {
      if (cover) {
        const [uploaded] = await uploadStudioFiles([cover.file]);
        Object.assign(body, { cover_image_url: uploaded.url, cover_width: cover.width, cover_height: cover.height, cover_size: cover.file.size });
      } else if (removeCover) {
        body.cover_image_url = "";
      }
      const page = await fanFundingApi.save(body);
      if (!mounted.current) return;
      toast.success("Your Fan Funding page has been saved.");
      onSaved(page);
    } catch (e) {
      if (!mounted.current) return;
      const message = e?.message && !e.response ? e.message : errorMessage(e, "We couldn't save your Fan Funding page. Please try again.");
      setErrors(/image|cover/i.test(message) && cover ? { cover: message } : /bio/i.test(message) ? { bio: message } : {});
      toast.error(`${message} Your saved details have not been changed.`);
    } finally {
      if (mounted.current) setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-7" data-testid="fan-page-tab">
      <Field label="Cover image" error={errors.cover}>
        <div
          className={`aspect-[3/1] w-full rounded-2xl border border-dashed flex items-center justify-center overflow-hidden bg-white ${errors.cover ? "border-red-400" : "border-gray-300"}`}
          data-testid="cover-preview"
        >
          {shownCover ? (
            <img src={shownCover} alt="Fan Page cover" className="w-full h-full object-cover" />
          ) : (
            <button type="button" className="text-sm text-gray-500 px-6 py-10 w-full h-full" onClick={() => inputRef.current && inputRef.current.click()} disabled={saving}>
              {rules.cover.recommended_width} × {rules.cover.recommended_height}
            </button>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp"
          aria-label="Cover image file"
          data-testid="cover-input"
          onChange={(e) => {
            choose(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <button type="button" className="text-sm font-semibold underline" style={{ color: ACCENT }} disabled={saving} onClick={() => inputRef.current && inputRef.current.click()} data-testid="cover-choose">
            {shownCover ? "Replace image" : "Upload image"}
          </button>
          {(cover || (saved.cover_image_url && !removeCover)) && (
            <button type="button" className="text-sm font-semibold underline text-gray-600" disabled={saving} onClick={() => (cover ? setCover(null) : setRemoveCover(true))}>
              {cover ? "Discard new image" : "Remove image"}
            </button>
          )}
          <span className="text-xs text-gray-500">
            Recommended {rules.cover.recommended_width} × {rules.cover.recommended_height} px. JPG, PNG or WebP, up to {Math.round(rules.cover.max_bytes / 1024 / 1024)} MB.
          </span>
        </div>
        {guidance && <p className="text-sm rounded-xl bg-amber-50 text-amber-900 px-4 py-3 mt-3" data-testid="cover-guidance">{guidance}</p>}
      </Field>

      <Field label="Fan page bio" error={errors.bio}>
        <textarea
          className={`w-full rounded-xl border bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2 ${errors.bio ? "border-red-400" : "border-gray-200"}`}
          style={{ "--tw-ring-color": ACCENT }}
          rows={4}
          placeholder="Tell fans what their support funds"
          value={bio}
          disabled={saving}
          aria-label="Fan page bio"
          data-testid="bio-input"
          onChange={(e) => setBio(e.target.value)}
        />
        <p className={`text-xs mt-1 text-right ${bio.length > rules.bio_max_length ? "text-red-600" : "text-gray-500"}`} data-testid="bio-count">
          {bio.length} / {rules.bio_max_length}
        </p>
      </Field>

      {dirty && <p className="text-xs text-amber-700" data-testid="unsaved-note">You have unsaved changes. They are not shown to fans until you save.</p>}
      <div className="flex justify-end gap-3">
        {dirty && <SecondaryButton onClick={cancel} disabled={saving} data-testid="fan-cancel">Cancel</SecondaryButton>}
        <PrimaryButton onClick={save} disabled={saving} data-testid="fan-save">{saving ? "Saving…" : "Save"}</PrimaryButton>
      </div>
    </div>
  );
}

const tableWrap = "rounded-2xl border border-gray-200 bg-white overflow-x-auto";
const headStyle = { color: ACCENT };

// Details of one support payment, read from the server. Payment method and fee only appear when the server sends them.
function SupportDetailDialog({ id, onClose }) {
  const [state, setState] = useState({ loading: true, failed: false, notFound: false, data: null });
  const load = useCallback(() => {
    setState({ loading: true, failed: false, notFound: false, data: null });
    fanFundingApi.fundingDetail(id).then(
      (data) => setState({ loading: false, failed: false, notFound: false, data }),
      (e) => setState({ loading: false, failed: true, notFound: e?.response?.status === 404, data: null })
    );
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const d = state.data;
  const rows = d
    ? [
        ["Supporter", supporterHandle(d.supporter)],
        [d.amount_basis === "net" ? "Amount (after fees)" : "Amount", `${money(d.amount, d.currency)} ${d.currency || ""}`.trim()],
        ...(d.fee !== undefined ? [["Transaction fee", money(d.fee, d.currency)]] : []),
        ["Message", MESSAGE_STATE_TEXT[d.message_state] || d.message || "—"],
        ["Date and time", formatFanDate(d.processed_at, true)],
        ["Transaction ID", d.reference || "—"],
        ["Payment status", d.status === "completed" ? "Completed" : d.status || "—"],
        ...(d.payment_method ? [["Payment method", d.payment_method]] : []),
      ]
    : [];

  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center bg-black/40 px-4 py-4 overflow-y-auto" role="presentation" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="support-detail-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="support-detail">
        <h2 id="support-detail-title" className="text-lg font-bold mb-4">Support details</h2>
        {state.loading && <div className="flex justify-center py-8"><Loading /></div>}
        {state.failed && (
          <div className="mb-4">
            <p className="text-gray-600 mb-3">{state.notFound ? "This support record could not be found." : "We couldn't load this support record."}</p>
            {!state.notFound && <PrimaryButton onClick={load}>Try again</PrimaryButton>}
          </div>
        )}
        {d && (
          <dl className="space-y-3 text-[15px] mb-6">
            {rows.map(([label, value]) => (
              <div key={label} className="flex justify-between gap-6">
                <dt className="text-gray-500 shrink-0">{label}</dt>
                <dd className="text-gray-900 text-right break-words min-w-0">{value}</dd>
              </div>
            ))}
          </dl>
        )}
        <div className="flex justify-end">
          <SecondaryButton onClick={onClose} data-testid="support-detail-close">Close</SecondaryButton>
        </div>
      </div>
    </div>
  );
}

// Report the message on a tip (FSD 6.7.2). The reason is optional. The message is hidden from the artist while it is reviewed.
function ReportMessageDialog({ row, onClose, onReported }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    setBusy(true);
    setError("");
    try {
      await fanFundingApi.reportTip(row.id, reason.trim() ? { reason: reason.trim() } : {});
      toast.success("Thank you. The message is hidden while we review it.");
      onReported();
    } catch (e) {
      if (e?.response?.status === 409) {
        onReported();
        return;
      }
      setError(errorMessage(e, "We couldn't send your report. Please try again."));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex [align-items:safe_center] justify-center bg-black/40 px-4 py-4 overflow-y-auto" role="presentation" onClick={busy ? undefined : onClose}>
      <div role="dialog" aria-modal="true" aria-labelledby="report-title" className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="report-dialog">
        <h2 id="report-title" className="text-lg font-bold mb-2">Report this message</h2>
        <p className="text-sm text-gray-600 mb-3">From {supporterHandle(row.supporter)}: <span className="text-gray-900">"{row.message}"</span></p>
        <label htmlFor="report-reason" className="block text-sm text-gray-600 mb-1">What is wrong with it? <span className="text-gray-400">(optional)</span></label>
        <textarea
          id="report-reason"
          rows={3}
          maxLength={REPORT_REASON_MAX}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full rounded-xl border border-gray-200 px-4 py-3 text-[15px]"
          data-testid="report-reason"
        />
        <p className="mt-1 text-xs text-gray-500">The message is hidden from you while our team reviews it.</p>
        {error && <p className="mt-2 text-sm text-red-600" data-testid="report-error">{error}</p>}
        <div className="mt-5 flex justify-end gap-3">
          <SecondaryButton onClick={onClose} disabled={busy} data-testid="report-cancel">Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={busy} data-testid="report-submit">{busy ? "Sending…" : "Report message"}</PrimaryButton>
        </div>
      </div>
    </div>
  );
}

const PAGE_SIZE = 20;

// Search and paging run on the server. The search box stays on screen while results load, so clearing it brings
// back the full list.
function FundingTab() {
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [state, setState] = useState({ loading: true, failed: false, data: null });
  const [open, setOpen] = useState(null);
  const [reporting, setReporting] = useState(null);
  const latest = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const load = useCallback(() => {
    const call = ++latest.current;
    setState((s) => ({ loading: true, failed: false, data: s.data }));
    fanFundingApi.funding({ search: search || undefined, page, limit: PAGE_SIZE }).then(
      (data) => call === latest.current && setState({ loading: false, failed: false, data }),
      () => call === latest.current && setState({ loading: false, failed: true, data: null })
    );
  }, [search, page]);
  useEffect(() => {
    load();
  }, [load]);

  const { data } = state;
  if (!data && state.loading) return <div className="flex justify-center py-16"><Loading /></div>;
  if (state.failed) {
    return (
      <div data-testid="funding-error">
        <p className="text-gray-600 mb-3">We couldn't load your support records.</p>
        <PrimaryButton onClick={load}>Try again</PrimaryButton>
      </div>
    );
  }

  const rows = data.supporters || [];
  const totalPages = data.pagination?.total_pages || 0;
  const searching = !!search;
  return (
    <div data-testid="funding-tab">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <input
          type="search"
          className="w-full max-w-xs rounded-xl border border-gray-200 bg-white px-4 py-3 text-[15px] focus:outline-none focus:ring-2"
          style={{ "--tw-ring-color": ACCENT }}
          placeholder="Search for supports"
          aria-label="Search for supports"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="supporter-search"
        />
        <p className="text-sm text-gray-600" data-testid="funding-received">
          Funding received: <span className="font-bold text-gray-900">{money(data.total?.total, data.total?.currency)}</span>
          <span className="block text-xs text-gray-500 sm:text-right">Successful payments only</span>
        </p>
      </div>
      <div className={`${tableWrap} ${state.loading ? "opacity-60" : ""}`} aria-busy={state.loading}>
        <table className="w-full text-left text-[15px]">
          <thead className="bg-[#f1f6f2]" style={headStyle}>
            <tr>
              <th className={TH}>Supporter</th>
              <th className={TH}>Amount</th>
              <th className={TH}>Message</th>
              <th className={TH}>Date</th>
              <th className={TH}>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-8 text-center text-gray-500" data-testid="funding-empty">
                  {searching ? "No support records found." : "No contributions have been received yet."}
                </td>
              </tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className="border-t border-gray-100" data-testid="funding-row">
                  <td className="px-6 py-3 font-semibold text-gray-900">{supporterHandle(r.supporter)}</td>
                  <td className="px-6 py-3 text-gray-900">{money(r.amount, r.currency)}</td>
                  <td className="px-6 py-3 text-gray-500" data-testid="funding-message">
                    {MESSAGE_STATE_TEXT[r.message_state] ? <span className="italic" data-testid="message-hidden">{MESSAGE_STATE_TEXT[r.message_state]}</span> : r.message || "—"}
                  </td>
                  <td className="px-6 py-3 text-gray-500 whitespace-nowrap">{formatFanDate(r.date)}</td>
                  <td className="px-6 py-3">
                    <button type="button" className="font-semibold" style={{ color: ACCENT }} onClick={() => setOpen(r.id)} data-testid="funding-view">
                      View
                    </button>
                    {r.reportable && (
                      <button type="button" className="ml-4 font-semibold text-gray-500 hover:text-red-600" onClick={() => setReporting(r)} data-testid="funding-report">
                        Report
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-3 mt-4 text-sm" data-testid="funding-pages">
          <SecondaryButton onClick={() => setPage((p) => p - 1)} disabled={page <= 1 || state.loading}>Previous</SecondaryButton>
          <span className="text-gray-600">Page {page} of {totalPages}</span>
          <SecondaryButton onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages || state.loading}>Next</SecondaryButton>
        </div>
      )}
      {open !== null && <SupportDetailDialog id={open} onClose={() => setOpen(null)} />}
      {reporting && <ReportMessageDialog row={reporting} onClose={() => setReporting(null)} onReported={() => { setReporting(null); load(); }} />}
    </div>
  );
}

const SUBSCRIBER_PAGE_SIZE = 20;

// Catalogue Subscribers: read-only. The list is fetched when the tab opens and again on Refresh or Try again (no live
// updates); someone who has unsubscribed is gone from the list after the next refresh. Paging runs on the server.
const SUBSCRIBER_TYPES = { follower: "Follower", member: "Member", follower_member: "Follower + Member" };
// FSD 6.7.3: followers and paying members are kept apart with a Type filter; members show Status, Since and Renews / ends.
// Lapsed members are no longer subscribers, so they only appear under Members or the Lapsed status.
const SUBSCRIBER_STATUS = { active: "Active", cancelling: "Cancelling", lapsed: "Lapsed" };
const SELECT_CLASS = "rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800";

function SubscribersTab() {
  const [page, setPage] = useState(1);
  const [type, setType] = useState("");
  const [status, setStatus] = useState("");
  const [state, setState] = useState({ loading: true, failed: false, data: null });
  const latest = useRef(0);

  const load = useCallback(() => {
    const call = ++latest.current;
    setState((s) => ({ loading: true, failed: false, data: s.data }));
    const params = { page, limit: SUBSCRIBER_PAGE_SIZE };
    if (type) params.type = type;
    if (status) params.status = status;
    fanFundingApi.subscribers(params).then(
      (data) => call === latest.current && setState({ loading: false, failed: false, data }),
      () => call === latest.current && setState({ loading: false, failed: true, data: null })
    );
  }, [page, type, status]);
  useEffect(() => {
    load();
  }, [load]);

  const { data } = state;
  if (!data && state.loading) return <div className="flex justify-center py-16" data-testid="subscribers-loading"><Loading /></div>;
  if (state.failed || !data) {
    return (
      <div data-testid="subscribers-error">
        <p className="text-gray-600 mb-3">We couldn't load your catalogue subscribers.</p>
        <PrimaryButton onClick={load}>Try again</PrimaryButton>
      </div>
    );
  }

  const rows = data.subscribers || [];
  const count = Number(data.count || 0);
  const totalPages = data.pagination?.total_pages || 0;
  const summary = data.summary;
  const filtered = !!(type || status);
  const changeType = (value) => { setType(value); setPage(1); };
  const changeStatus = (value) => { setStatus(value); setPage(1); };
  return (
    <div data-testid="subscribers-tab">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-lg font-bold text-gray-900 mb-1">Catalogue Subscribers</h2>
          <p className="text-gray-600">
            People subscribed to your catalogue for updates on new releases.{" "}
            <span className="text-gray-900 font-semibold" data-testid="subscribers">{count.toLocaleString()} {count === 1 ? "subscriber" : "subscribers"}</span>
          </p>
        </div>
        <SecondaryButton onClick={load} disabled={state.loading} data-testid="subscribers-refresh">Refresh</SecondaryButton>
      </div>
      {summary && (
        <p className="text-sm text-gray-600 mb-3" data-testid="subscribers-summary">
          {Number(summary.followers || 0).toLocaleString()} followers · {Number(summary.members_active || 0).toLocaleString()} active members ·{" "}
          {Number(summary.members_cancelling || 0).toLocaleString()} cancelling · {Number(summary.members_lapsed || 0).toLocaleString()} lapsed
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <label className="text-sm text-gray-600">
          Type{" "}
          <select className={SELECT_CLASS} value={type} onChange={(e) => changeType(e.target.value)} data-testid="subscribers-type">
            <option value="">All</option>
            <option value="followers">Followers</option>
            <option value="members">Members</option>
          </select>
        </label>
        <label className="text-sm text-gray-600">
          Status{" "}
          <select className={SELECT_CLASS} value={status} onChange={(e) => changeStatus(e.target.value)} data-testid="subscribers-status">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="cancelling">Cancelling</option>
            <option value="lapsed">Lapsed</option>
          </select>
        </label>
      </div>
      <div className={`${tableWrap} ${state.loading ? "opacity-60" : ""}`} aria-busy={state.loading}>
        <table className="w-full text-left text-[15px]">
          <thead className="bg-[#f1f6f2]" style={headStyle}>
            <tr>
              <th className={TH}>Subscriber</th>
              <th className={TH}>Type</th>
              <th className={TH}>Status</th>
              <th className={TH}>Since</th>
              <th className={TH}>Renews / ends</th>
              <th className={TH}>Joined</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-gray-500" data-testid="subscribers-empty">{filtered ? "No subscribers match these filters." : "No catalogue subscribers yet."}</td>
              </tr>
            ) : (
              rows.map((s, i) => (
                <tr key={`${s.username}-${s.joined_at}-${i}`} className="border-t border-gray-100" data-testid="subscriber-row">
                  <td className="px-6 py-3 font-semibold text-gray-900">{s.username || "—"}</td>
                  <td className="px-6 py-3 text-gray-700 whitespace-nowrap">{SUBSCRIBER_TYPES[s.type] || "Follower"}</td>
                  <td className="px-6 py-3 text-gray-700 whitespace-nowrap" data-testid="subscriber-status">{SUBSCRIBER_STATUS[s.status] || "Active"}</td>
                  <td className="px-6 py-3 text-gray-500 whitespace-nowrap" data-testid="subscriber-since">{s.member_since ? formatJoinedDate(s.member_since) : "—"}</td>
                  <td className="px-6 py-3 text-gray-500 whitespace-nowrap" data-testid="subscriber-renews">
                    {s.status === "cancelling" && s.ends_on
                      ? `Ends ${formatJoinedDate(s.ends_on)}`
                      : s.renews_on
                        ? `Renews ${formatJoinedDate(s.renews_on)}`
                        : s.lapsed_on
                          ? `${s.status === "lapsed" ? "Lapsed" : "Membership lapsed"} ${formatJoinedDate(s.lapsed_on)}`
                          : "—"}
                  </td>
                  <td className="px-6 py-3 text-gray-500 whitespace-nowrap">{formatJoinedDate(s.joined_at)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-3 mt-4 text-sm" data-testid="subscribers-pages">
          <SecondaryButton onClick={() => setPage((p) => p - 1)} disabled={page <= 1 || state.loading}>Previous</SecondaryButton>
          <span className="text-gray-600">Page {page} of {totalPages}</span>
          <SecondaryButton onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages || state.loading}>Next</SecondaryButton>
        </div>
      )}
    </div>
  );
}

function FanFundingBody() {
  const [tab, setTab] = useState("page");
  const [page, setPage] = useState({ loading: true, failed: false, saved: null });

  const load = useCallback(() => {
    setPage({ loading: true, failed: false, saved: null });
    fanFundingApi.get().then(
      (saved) => setPage({ loading: false, failed: false, saved }),
      () => setPage({ loading: false, failed: true, saved: null })
    );
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  if (page.loading) return <div className="flex justify-center py-16"><Loading /></div>;
  if (page.failed || !page.saved) {
    return (
      <div>
        <p className="text-gray-600 mb-3">We couldn't load your Fan Funding page.</p>
        <PrimaryButton onClick={load}>Try again</PrimaryButton>
      </div>
    );
  }

  return (
    <div data-testid="fan-funding">
      <Tabs active={tab} onChange={setTab} />
      {tab === "page" && <FanPageTab key={page.saved.updated_at || "new"} saved={page.saved} onSaved={(saved) => setPage({ loading: false, failed: false, saved })} />}
      {tab === "funding" && <FundingTab />}
      {tab === "subscribers" && <SubscribersTab />}
    </div>
  );
}

export default function FanFunding() {
  const account = useStudioAccount();
  return (
    <StudioGate account={account} active="funding" heading="Fan Funding" tabs={[]} maxWidth="max-w-5xl">
      <FanFundingBody />
    </StudioGate>
  );
}
